import { clsx } from "clsx";
import { forwardRef, InputHTMLAttributes, useState } from "react";

import { isTouchDevice } from "@smart/itops-utils-basic";

import { Icon } from "../../icon";

export const FileInputField = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement>
>((props, ref) => (
  <input
    {...props}
    ref={ref}
    className="absolute top-0 right-0 border-0 h-0 w-0 opacity-0 overflow-hidden outline-none"
  />
));

type FileUploadProps = {
  id: string;
  errorId: string;
  error?: string;
  sizeLimit: string;
  onSelect: (files?: FileList) => void;
  isMultiFile?: boolean;
  disabled?: boolean;
  className?: string;
};

export const FileUpload = ({
  id,
  error,
  errorId,
  sizeLimit,
  onSelect,
  isMultiFile = true,
  disabled,
  className,
}: FileUploadProps) => {
  const [dragging, setDragging] = useState(false);
  const isTouch = isTouchDevice();
  const fileText = isMultiFile ? "files" : "file";

  return (
    <label
      className={clsx(
        "m-0 border-2 border-dashed focus-within:shadow-[0px_0px_0px_4px_rgba(174,211,254,0.6)] focus-within:border-blue-560",
        "rounded outline-none cursor-pointer flex gap-small flex-col items-center p-[1.2rem]",
        dragging
          ? "bg-blue-60 border-blue-560"
          : "bg-neutral-20 border-neutral-200",
        error && "border-red-550 shadow-error",
        disabled && "text-gray-300",
        className,
      )}
      htmlFor={id}
      aria-invalid={!!error}
      aria-errormessage={error ? errorId : undefined}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        onSelect(e.dataTransfer.files);
      }}
    >
      <div className="w-[3.2rem] h-[3.2rem] bg-black/10 rounded flex justify-center items-center">
        <Icon name="solidSquareUp" className="w-[1.4rem]" />
      </div>
      <p>
        <span
          className={clsx(
            "text-ui font-normal",
            disabled ? "text-gray-300" : "text-blue-560",
          )}
        >
          {isTouch ? "Tap" : "Browse"}
        </span>{" "}
        <span className="text-ui font-semibold">
          {isTouch ? `to upload ${fileText}` : `or drag ${fileText} here`}
        </span>
      </p>
      <p
        className={clsx(
          "text-caption font-normal",
          disabled ? "text-gray-300" : "text-neutral-600",
        )}
      >
        {sizeLimit}
      </p>
    </label>
  );
};
